import classNames from 'classnames';
import React, { ReactNode, useState } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Button, ButtonMode } from '../Components/Button';
import { ArrowLeftIcon, MedicationListIcon, PlusIcon } from '../Components/Icons';
import { NavMenu, NavMenuItem } from '../Components/NavMenu/NavMenu';
import { IconProps } from '../Components/StyledIcon';
import { CreateMedicationListDialog } from '../MedicationLists/CreateMedicationListDialog/CreateMedicationListDialog';
import { MedicationList } from '../MedicationLists/MedicationList/MedicationList';
import { MedicationLists } from '../MedicationLists/MedicationLists';
import {
    createRouteForMedicationList,
    routeForDashboard,
    routeForMedicationList,
    routeForMedicationLists,
    routeForNewMedicationList
} from '../Routes';
import { body, white } from '../Styles/Colours';
import { MedicationsDashboard } from './MedicationsDashboard';
import styles from './MedicationsFeature.module.scss';

const iconProps: IconProps = {
    size: '24',
    color: body
};

function MedicationsFeatureLeftMenu(props: { onNavigate?(): void }) {
    const [menuCollapsed, setMenuCollapsed] = useState<boolean>(false);
    const location = useLocation();

    const sidebarComponent = (
        <NavMenu isCollapsed={menuCollapsed}>
            <NavMenuItem
                onClick={props.onNavigate}
                icon={<MedicationListIcon {...iconProps} />}
                title={'Medication lists'}
                exact
                pathname={location.pathname}
                location={routeForMedicationLists}
                referrerLocation={location}
                testId={'med-lists'}
            />
        </NavMenu>
    );

    const sidebarClass = classNames(styles.sidebar, {
        [styles.sidebarCollapsed]: menuCollapsed
    });

    const sidebarToggleClass = classNames(styles.closeButton, {
        [styles.closeButtonCollapsed]: menuCollapsed
    });

    const sidebarToggleTitle = menuCollapsed ? 'Expand menu' : '';

    return (
        <div className={sidebarClass}>
            <div className={styles.sidebarMenu}>{sidebarComponent}</div>
            <div className={styles.sidebarCollapseToggle}>
                <Button
                    testId={'sidebar-collapse'}
                    mode={ButtonMode.text}
                    className={sidebarToggleClass}
                    title={sidebarToggleTitle}
                    startIcon={<ArrowLeftIcon className={styles.sidebarToggleIcon} color={body} size={'24'} />}
                    onClick={() => setMenuCollapsed(!menuCollapsed)}
                >
                    {!menuCollapsed && <div className={styles.sidebarToggleText}>Collapse menu</div>}
                </Button>
            </div>
        </div>
    );
}

export const SimpleLayout: React.FC<{
    leftPanel: ReactNode;
}> = props => {
    return (
        <div className={styles.twoPanelLayout}>
            <div>{props.leftPanel}</div>
            <div className={styles.contentsPanel}>{props.children}</div>
        </div>
    );
};

export default function MedicationsFeature() {
    const history = useHistory();

    return (
        <div className={styles.container}>
            <SimpleLayout leftPanel={<MedicationsFeatureLeftMenu />}>
                <Switch>
                    <Route exact path={routeForDashboard} component={MedicationsDashboard} />

                    <Route
                        exact
                        path={routeForMedicationList}
                        render={routeParams => {
                            const medListId = routeParams.match.params.medListId;
                            if (medListId)
                                return (
                                    <MedicationList
                                        medListId={medListId}
                                        onDelete={() => {
                                            console.log('NOOP');
                                        }}
                                    />
                                );
                        }}
                    />
                    <Route path={routeForMedicationLists}>
                        <div className={styles.medicationListWrapper}>
                            <div className={styles.header}>
                                <Button
                                    onClick={() => history.push(routeForNewMedicationList)}
                                    testId='create-medlist'
                                    className={styles.addList}
                                    startIcon={<PlusIcon size='16' color={white} />}
                                >
                                    Add medication list
                                </Button>
                            </div>
                            <MedicationLists
                                onAddMedicationList={() => {
                                    history.push(routeForNewMedicationList);
                                }}
                                onEditClick={row => {
                                    history.push(createRouteForMedicationList(row.id));
                                }}
                                filtersActive={false}
                                query=''
                            >
                                {medicationListHelpers => (
                                    <Route exact path={routeForNewMedicationList}>
                                        <CreateMedicationListDialog
                                            onClose={() => {
                                                history.push(routeForMedicationLists);
                                            }}
                                            onComplete={(id: string) => {
                                                medicationListHelpers.refresh();
                                                history.push(createRouteForMedicationList(id));
                                            }}
                                        />
                                    </Route>
                                )}
                            </MedicationLists>
                        </div>
                    </Route>
                </Switch>
            </SimpleLayout>
        </div>
    );
}
